import { type ReactElement } from 'react';
import { FileText, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V2File } from '../v2Upload.d';
import { isCamFile } from '@/workers/camFiles';
import { CAD_EXTS } from '@/redux/post/post.constants';
import { SectionPanel, type SectionAddProps } from './FileGrids';
import { FileMarkIcon, docKindOf, fileMarkFor } from '@/lib/fileType';
import { NoCadArt, NoDocumentationArt, NoVectorsArt } from './EmptySectionArt';

/**
 * The three kinds of supporting file, by extension.
 *
 * A file that matches nothing lands in Documentation — the catch-all, and the
 * honest answer for a format the wizard has never seen.
 */
const CATEGORIES: Array<{
  title: string;
  hint: string;
  /** What the list DISPLAYS. Documentation claims nothing, so it shows every
   * supporting file the other two don't — a `.txt` or a `.zip` dropped on the
   * step still has somewhere to appear. */
  exts: string[];
  Empty: () => ReactElement;
}> = [
  {
    Empty: NoCadArt,
    title: 'CAD Files',
    hint: 'CAM projects and 3D models',
    exts: CAD_EXTS,
  },
  {
    Empty: NoVectorsArt,
    title: 'Vectors',
    hint: 'SVG, DXF and related artwork',
    exts: ['svg', 'dxf', 'dwg', 'ai', 'eps'],
  },
  {
    Empty: NoDocumentationArt,
    title: 'Documentation',
    hint: 'PDFs and instructions',
    exts: [],
  },
];

/** Which list a file belongs in. */
function categoryOf(file: V2File): string {
  const match = CATEGORIES.find((c) => c.exts.includes(file.ext));
  return (match ?? CATEGORIES[CATEGORIES.length - 1]).title;
}

/** The mark a row leads with: its authoring software where the extension says
 * so, else the glyph for its kind. An unknown extension is still a document
 * here, unlike the download modal, which has a resource type to fall back on. */
function RowMark({ file }: { file: V2File }) {
  const mark = fileMarkFor(file.ext) ?? docKindOf(file.ext);
  return <FileMarkIcon mark={mark} className="h-4 w-4" />;
}

/** One file: its mark, the name, and a way to take it back out. A parsed CAM
 * design opens its read-out; every other doc has nothing to show, so only that
 * one is a button. */
const DOC_ROW =
  'flex w-full items-center gap-2.5 rounded-lg border bg-card px-2.5 py-2 text-left';

/** The delete affordance, shared by both row shapes. `stopPropagation` because
 * an openable row is itself a button — without it, deleting also opens. */
function DeleteButton({
  file,
  onDelete,
}: {
  file: V2File;
  onDelete: () => void;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}
      aria-label={`Delete ${file.name}`}
      className="shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

/** One file: its mark, the name, and a way to take it back out.
 *
 * A parsed CAM design opens its read-out and so is a button end to end — the
 * filename alone is 12px of text in a 36px row, which leaves most of what
 * looks clickable dead to the pointer. Every other doc has nothing to show,
 * so it stays a plain row.
 */
function DocRow({
  file,
  onOpen,
  onDelete,
}: {
  file: V2File;
  onOpen?: () => void;
  onDelete: () => void;
}) {
  const body = (
    <>
      <RowMark file={file} />
      <span
        title={file.name}
        className="min-w-0 flex-1 truncate font-tertiary text-[12px]"
      >
        {file.name}
      </span>
    </>
  );

  if (!onOpen) {
    return (
      <div
        data-testid={`v2-file-card-${file.name}`}
        className={cn('group', DOC_ROW)}
      >
        {body}
        <DeleteButton file={file} onDelete={onDelete} />
      </div>
    );
  }

  return (
    <div
      data-testid={`v2-file-card-${file.name}`}
      className="group relative flex items-center"
    >
      <button
        type="button"
        onClick={onOpen}
        title={`View details for ${file.name}`}
        className={cn(
          DOC_ROW,
          'cursor-pointer transition-colors hover:border-border-strong hover:bg-muted/40'
        )}
      >
        {body}
        {/* Space the delete button sits over, so it never overlaps the name. */}
        <span className="w-3.5 shrink-0" />
      </button>
      <span className="absolute right-2.5 flex items-center">
        <DeleteButton file={file} onDelete={onDelete} />
      </span>
    </div>
  );
}

function DocList({
  category,
  files,
  onOpenFile,
  onDeleteFile,
}: {
  category: (typeof CATEGORIES)[number];
  files: V2File[];
  onOpenFile: (fileId: string) => void;
  onDeleteFile: (fileId: string) => void;
}) {
  const { title, hint } = category;

  return (
    <div
      data-testid={`v2-doc-list-${title}`}
      className="rounded-xl border bg-muted/20 p-3"
    >
      <div className="mb-2.5">
        <h4 className="font-secondary text-[13px] font-semibold">{title}</h4>
        <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>
      </div>

      {files.length === 0 ? (
        <category.Empty />
      ) : (
        <div className="space-y-1.5">
          {files.map((file) => (
            <DocRow
              key={file.id}
              file={file}
              onOpen={
                isCamFile(file.name) ? () => onOpenFile(file.id) : undefined
              }
              onDelete={() => onDeleteFile(file.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * CAD, vectors and documentation as three lists in one panel.
 *
 * Lists rather than the card grid the photos use: these files have no preview
 * worth 200px of height — a `.crv` is a grey icon whichever way it is drawn —
 * so the icon, the name and a delete is all a row can honestly carry.
 */
export default function DocLists({
  files,
  onClear,
  onOpenFile,
  onDeleteFile,
  ...add
}: SectionAddProps & {
  files: V2File[];
  onClear: () => void;
  onOpenFile: (fileId: string) => void;
  onDeleteFile: (fileId: string) => void;
}) {
  return (
    <SectionPanel
      icon={FileText}
      title="CAD files & docs"
      help="Source files and setup instructions, so makers can open the project in their own CAM instead of starting from your G-code."
      count={files.length}
      badge={
        <span className="rounded-full bg-muted px-2.5 py-1 text-[12px] font-tertiary font-medium text-muted-foreground">
          Optional
        </span>
      }
      clearLabel="Remove all"
      onClear={onClear}
      addLabel="Add CAD & docs"
      {...add}
    >
      <p className="mb-3 text-sm text-muted-foreground">
        CAM projects, vector drawings, 3D models and instructions — these
        download with the project.
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {CATEGORIES.map((category) => (
          <DocList
            key={category.title}
            category={category}
            files={files.filter((f) => categoryOf(f) === category.title)}
            onOpenFile={onOpenFile}
            onDeleteFile={onDeleteFile}
          />
        ))}
      </div>
    </SectionPanel>
  );
}
